import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  FaGithub,
  FaLinkedin,
  FaDownload,
  FaChartLine,
  FaShieldAlt,
  FaCode,
} from "react-icons/fa";
import { motion } from "framer-motion";
import AnimatedTyping from "../components/AnimatedTyping";
import EnhancedButton from "../components/EnhancedButton";
import FeaturedProject from "../components/FeaturedProject";
import RecentBuilds from "../components/RecentBuilds";
import RevealOnScroll from "../components/RevealOnScroll";
import { getRecentActivity } from "../api/activityAPI";
import logo from "../components/images/logo2026.png";

const heroStagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const heroItem = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const personJsonLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "David Seibold",
  alternateName: "Davey Rockets",
  url: "https://daveyrockets.me",
  jobTitle: "Full-Stack Engineer",
  sameAs: [
    "https://github.com/PenneconDavid",
    "https://linkedin.com/in/davidseibold",
  ],
};

function Home() {
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    getRecentActivity()
      .then(setActivity)
      .catch(() => setActivity(null))
      .finally(() => setActivityLoading(false));
  }, []);

  const handleDownloadCV = () => {
    const timestamp = new Date().getTime();
    const cvUrl = `/DavidSeibold-Resume.pdf?v=${timestamp}`;
    const link = document.createElement("a");
    link.href = cvUrl;
    link.download = "DavidSeibold-Resume.pdf";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Helmet>
        <title>David Seibold - Full-Stack Engineer Portfolio</title>
        <meta
          name="description"
          content="Portfolio site of David Seibold, showcasing full-stack projects, blogs, and professional skills. Connect with me to learn more about my journey from M&A to software engineering."
        />
        <meta
          name="keywords"
          content="David Seibold, full-stack engineer, software engineer, TypeScript, Next.js, portfolio, M&A, projects"
        />
        <meta name="author" content="David Seibold" />
        <meta
          property="og:title"
          content="David Seibold - Full-Stack Engineer Portfolio"
        />
        <meta
          property="og:description"
          content="Explore my journey from M&A to software engineering. View my projects and connect!"
        />
        <meta
          property="og:image"
          content="https://daveyrockets.me/og-banner.png"
        />
        <meta
          name="twitter:image"
          content="https://daveyrockets.me/og-banner.png"
        />
        <meta property="og:url" content="https://daveyrockets.me" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(personJsonLd)}</script>
      </Helmet>
      <div className="relative pb-80 min-h-screen flex flex-col justify-between overflow-hidden text-gray-800 dark:text-brand-cream">
        <motion.div
          variants={heroStagger}
          initial="hidden"
          animate="show"
          className="relative z-10 mx-auto max-w-4xl px-4 pt-12"
        >
          {/* Hero */}
          <motion.div variants={heroItem} className="flex justify-center mb-6">
            <img
              src={logo}
              alt="David Seibold's personal logo"
              className="w-32 h-32 sm:w-44 sm:h-44 rounded-full shadow-xl shadow-gray-300 dark:shadow-black"
            />
          </motion.div>

          <motion.h1
            variants={heroItem}
            className="text-3xl md:text-5xl my-4 md:my-8 font-display text-center font-bold"
          >
            <span className="text-gray-800 dark:text-brand-cream">
              I'm David, my friends call me{" "}
            </span>
            <span className="text-gradient">
              <AnimatedTyping
                text="Davey Rockets"
                speed={150}
                className="inline"
              />
            </span>
            <span className="text-gray-800 dark:text-brand-cream">, and I'm a </span>
            <span className="text-gradient-reverse">
              <AnimatedTyping
                text="Full-Stack Engineer"
                speed={120}
                delay={2000}
                className="inline"
              />
            </span>
          </motion.h1>

          <motion.div
            variants={heroItem}
            className="flex justify-center space-x-8 mb-10"
          >
            <a
              href="https://github.com/PenneconDavid"
              target="_blank"
              rel="noopener noreferrer"
              className="group p-3 rounded-full bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-300 dark:border-gray-700 hover:border-brand-gold transition-all duration-300 hover:shadow-glow"
              aria-label="GitHub profile"
            >
              <FaGithub className="text-2xl text-gray-700 dark:text-gray-300 group-hover:text-brand-gold transition-colors duration-300" />
            </a>
            <a
              href="https://linkedin.com/in/davidseibold"
              target="_blank"
              rel="noopener noreferrer"
              className="group p-3 rounded-full bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-300 dark:border-gray-700 hover:border-brand-gold transition-all duration-300 hover:shadow-glow"
              aria-label="LinkedIn profile"
            >
              <FaLinkedin className="text-2xl text-gray-700 dark:text-gray-300 group-hover:text-brand-gold transition-colors duration-300" />
            </a>
          </motion.div>

          <motion.div
            variants={heroItem}
            className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12"
          >
            <EnhancedButton
              href="/contact"
              variant="primary"
              size="lg"
              className="min-w-[200px]"
            >
              Get in Touch
            </EnhancedButton>
            <EnhancedButton
              onClick={handleDownloadCV}
              variant="outline"
              size="lg"
              icon={<FaDownload />}
              className="min-w-[200px]"
            >
              Download CV
            </EnhancedButton>
          </motion.div>

          {/* Featured project */}
          <FeaturedProject
            project={activity?.featuredProject}
            loading={activityLoading}
          />

          {/* Recent activity strip (replaces GitHub language stats) */}
          <RecentBuilds activity={activity} loading={activityLoading} />

          {/* Finance proof stats — trimmed to two high-signal metrics */}
          <RevealOnScroll className="mb-12 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-brand-darker dark:to-[#2a2a2a] p-6 rounded-lg border border-gray-200 dark:border-brand-purple/20 hover:border-brand-gold/40 transition-all duration-300 hover:shadow-glow text-center">
              <div className="text-3xl md:text-4xl font-bold text-brand-gold mb-2">
                200+
              </div>
              <div className="text-sm md:text-base text-gray-600 dark:text-brand-cream/80">
                Acquisitions Led
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-white dark:from-brand-darker dark:to-[#2a2a2a] p-6 rounded-lg border border-gray-200 dark:border-brand-purple/20 hover:border-brand-gold/40 transition-all duration-300 hover:shadow-glow text-center">
              <div className="text-3xl md:text-4xl font-bold text-brand-gold mb-2">
                $300M+
              </div>
              <div className="text-sm md:text-base text-gray-600 dark:text-brand-cream/80">
                Deals Analyzed
              </div>
            </div>
          </RevealOnScroll>

          {/* Value proposition */}
          <RevealOnScroll className="mb-12 bg-gradient-to-br from-gray-50 to-white dark:from-brand-darker dark:to-[#2a2a2a] p-8 md:p-12 rounded-lg border border-gray-200 dark:border-brand-purple/20">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center text-gradient">
              Why Finance + Engineering?
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <FaChartLine className="text-4xl text-brand-gold mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2 text-brand-gold">
                  Financial Acumen
                </h4>
                <p className="text-gray-600 dark:text-brand-cream/80 text-sm md:text-base">
                  Deep understanding of financial systems, risk assessment, and
                  business valuation from 11 years in healthcare M&A
                </p>
              </div>
              <div className="text-center">
                <FaCode className="text-4xl text-brand-gold mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2 text-brand-gold">
                  Technical Excellence
                </h4>
                <p className="text-gray-600 dark:text-brand-cream/80 text-sm md:text-base">
                  Production-grade TypeScript, Next.js, and Node.js apps, plus
                  smart contracts when the work is onchain, all shipped with tests
                  and CI
                </p>
              </div>
              <div className="text-center">
                <FaShieldAlt className="text-4xl text-brand-gold mx-auto mb-4" />
                <h4 className="text-xl font-semibold mb-2 text-brand-gold">
                  Security First
                </h4>
                <p className="text-gray-600 dark:text-brand-cream/80 text-sm md:text-base">
                  Applying due diligence rigor to auth boundaries, input
                  scanning, CI gates, and smart contract design
                </p>
              </div>
            </div>
          </RevealOnScroll>
        </motion.div>
      </div>
    </>
  );
}

export default Home;
